import {
  BellIcon as BellIconOutline,
  BookmarkIcon as BookmarkIconOutline,
  PlayCircleIcon as PlayCircleIconOutline,
  TrashIcon as TrashIconOutline
} from '@heroicons/react/24/outline'
import {
  BellIcon as BellIconSolid,
  BookmarkIcon as BookmarkIconSolid,
  PlayCircleIcon as PlayCircleIconSolid,
  TrashIcon as TrashIconSolid
} from '@heroicons/react/24/solid'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { SPACE_ACTION_IDS } from '../constants/space-action-ids'
import { useNotificationContext } from '../context'
import {
  ARCHIVED_CAREER_RESTORE,
  RECOVER_CAREERS_PATH,
  resolveSpaceActionAvailability
} from '../features/pricing/archived-careers'
import { useArchivedCareerIds } from './use-archived-career-ids'

const ACTIONS = {
  SEND_REMINDER: 'SEND_REMINDER',
  RESET_EXPIRED: 'RESET_EXPIRED',
  SET_SPACE_STATE: 'SET_SPACE_STATE',
  DELETE_SPACE: 'DELETE_SPACE',
  FULL_RESET: 'FULL_RESET'
}

function combinations(arr) {
  const result = []

  for (let i = 0; i < Math.pow(2, arr.length); i++) {
    const combo = []
    for (let j = 0; j < arr.length; j++) {
      if ((i & (1 << j)) !== 0) {
        combo.push(arr[j])
      }
    }
    result.push(combo)
  }

  return result
}

// Hinweis bei Klick auf eine nicht verfügbare Aktion. Der eigene Hinweis der
// Aktion (archivierte Stelle, D16) geht vor, sonst gilt der allgemeine des
// Aufrufers.
export const withNotAvailableHint = (action, fallback) => ({
  ...action,
  handleActionNotAvailable: action.handleActionNotAvailable || fallback
})

export const useSpaceActions = ({ spaces }) => {
  const { t } = useTranslation()
  const { alert } = useNotificationContext()
  const navigate = useNavigate()

  const { archivedCareerIds } = useArchivedCareerIds({ spaces })

  const [displaySendReminder, setDisplaySendReminder] = useState(false)
  const [displaySetSpaceState, setDisplaySetSpaceState] = useState(false)
  const [displayDeleteSpace, setDisplayDeleteSpace] = useState(false)
  const [displayResetExpired, setDisplayResetExpired] = useState(false)
  const [displayFullReset, setDisplayFullReset] = useState(false)

  // Nur die Assessment-Achse entscheidet über Erinnern und Verlängern. Der
  // Talent-Status (SMART_PREDICT) ist seit er jederzeit gesetzt werden kann
  // unabhängig davon und darf diese Aktionen nicht sperren.
  const uniqueLastInteractionKeys = useMemo(() => {
    // _* = _EMAIL && _LINK
    // ASSESSMENT_COMPLETED STARTED_VIA_* REMINDED INVITED_VIA_* EXPIRED_VIA_*
    // RESET_EXPIRED_* EMAIL_BOUNCED
    const assessment_status_keys = spaces.map(
      ({ assessment_status }) => assessment_status.key
    )

    return [...new Set(assessment_status_keys)].sort()
  }, [spaces])

  const sendSpacesReminderAvailable = useMemo(() => {
    // all spaces last interaction keys includes only
    const arr = [
      'INVITED_VIA_EMAIL',
      'EXPIRED_VIA_EMAIL',
      'RESET_EXPIRED_VIA_EMAIL',
      'STARTED_VIA_EMAIL',
      'REMINDED'
    ]

    return combinations(arr).some(
      (comb) =>
        JSON.stringify(comb.sort()) ===
        // uniqueLastInteractionKeys already sorted!
        JSON.stringify(uniqueLastInteractionKeys)
    )
  }, [uniqueLastInteractionKeys])

  const resetExpiredAvailable = useMemo(() => {
    // all spaces last interaction keys includes only
    const arr = [
      'INVITED_VIA_EMAIL',
      'INVITED_VIA_LINK',
      'EXPIRED_VIA_EMAIL',
      'EXPIRED_VIA_LINK',
      'RESET_EXPIRED_VIA_EMAIL',
      'RESET_EXPIRED_VIA_LINK',
      'STARTED_VIA_EMAIL',
      'STARTED_VIA_LINK',
      'REMINDED'
    ]

    return combinations(arr).some(
      (comb) =>
        JSON.stringify(comb.sort()) ===
        // uniqueLastInteractionKeys already sorted!
        JSON.stringify(uniqueLastInteractionKeys)
    )
  }, [uniqueLastInteractionKeys])

  const mapAction = ({ action }) => {
    switch (action) {
      case ACTIONS.SET_SPACE_STATE:
        return {
          key: action,
          id: SPACE_ACTION_IDS.SET_SPACE_STATE,
          label: t('space_actions.label_' + action),
          icons: { solid: BookmarkIconSolid, outline: BookmarkIconOutline },
          onClick: () => setDisplaySetSpaceState(true),
          // Der Talent-Status lässt sich jederzeit setzen, auch bei
          // abgelaufenem oder nicht gestartetem Assessment.
          available: true
        }
      case ACTIONS.SEND_REMINDER:
        return {
          key: action,
          id: SPACE_ACTION_IDS.SEND_REMINDER,
          label: t('space_actions.label_' + action),
          icons: { solid: BellIconSolid, outline: BellIconOutline },
          onClick: () => setDisplaySendReminder(true),
          available: sendSpacesReminderAvailable
        }
      case ACTIONS.DELETE_SPACE:
        return {
          key: action,
          id: SPACE_ACTION_IDS.DELETE_SPACE,
          label: t('space_actions.label_' + action),
          icons: { solid: TrashIconSolid, outline: TrashIconOutline },
          onClick: () => setDisplayDeleteSpace(true),
          available: true
        }
      case ACTIONS.RESET_EXPIRED:
        return {
          key: action,
          id: SPACE_ACTION_IDS.RESET_EXPIRED,
          label: t('space_actions.label_' + action),
          icons: { solid: PlayCircleIconSolid, outline: PlayCircleIconOutline },
          onClick: () => setDisplayResetExpired(true),
          available: resetExpiredAvailable
        }
      case ACTIONS.FULL_RESET:
        return {
          key: action,
          id: SPACE_ACTION_IDS.FULL_RESET,
          label: t('space_actions.label_' + action),
          icons: { solid: PlayCircleIconSolid, outline: PlayCircleIconOutline },
          onClick: () => setDisplayFullReset(true),
          available: true
        }
      default:
        throw new Error(`action not found - ${action}`)
    }
  }

  // Archivierte Stelle (D16, jeder Modus). Ohne archivierte Stelle bleibt die
  // Aktion unverändert. Sonst ist sie nicht verfügbar und bringt ihren eigenen
  // Hinweis mit, den der Aufrufer statt des allgemeinen zeigt.
  const displayArchivedCareerHint = (hintKey) => {
    alert(
      t('talents.action_not_availabe.modal_title'),
      [t(hintKey)],
      [
        {
          text: t(ARCHIVED_CAREER_RESTORE),
          onPress: () => navigate(RECOVER_CAREERS_PATH)
        },
        {
          text: t('talents.action_not_availabe.modal_close_action'),
          style: 'cancel'
        }
      ]
    )
  }

  const withArchivedCareerBlock = (mapped) => {
    const { available, hintKey } = resolveSpaceActionAvailability({
      action: mapped.key,
      available: mapped.available,
      spaces,
      archivedCareerIds
    })

    if (!hintKey) return mapped

    return {
      ...mapped,
      available,
      handleActionNotAvailable: () => displayArchivedCareerHint(hintKey)
    }
  }

  return {
    actions: Object.keys(ACTIONS).map((action) =>
      withArchivedCareerBlock(mapAction({ action }))
    ),
    displaySendReminder,
    setDisplaySendReminder,
    displaySetSpaceState,
    setDisplaySetSpaceState,
    displayDeleteSpace,
    setDisplayDeleteSpace,
    displayResetExpired,
    setDisplayResetExpired,
    displayFullReset,
    setDisplayFullReset
  }
}
